import { useEffect, useState } from "react";
import { Mascot } from "@/components/brand/Mascot";
import { ParentMascot } from "@/components/brand/ParentMascot";
import { SpeechBubble } from "@/components/brand/SpeechBubble";
import { PhilosophyCards } from "@/components/brand/PhilosophyCards";
import { Button } from "@/components/ui/Button";
import { Confetti } from "@/components/effects/Confetti";
import {
  CONTEXT_PRESETS,
  type MascotContext,
  toneBackground,
  toneTextColor,
} from "@/lib/mascot/contexts";
import { momentCopy, type MomentData, type MomentStep } from "@/lib/mascot/copy";
import { BLOG_PHILOSOPHY_URL } from "@/lib/content/philosophy";
import { useAnimatedNumber } from "@/lib/animations/useAnimatedNumber";
import { cn } from "@/lib/utils/cn";

// Konteksty z dużym celebration vibe — pokazują konfetti.
const CONFETTI_CONTEXTS = new Set<MascotContext>([
  "level-up",
  "goal-reached",
  "first-payout",
  "streak-milestone",
  "badge-unlocked",
]);

type Props = {
  context: MascotContext;
  open: boolean;
  onClose: () => void;
  data?: MomentData;
  /** Czy ten moment używa formy żeńskiej czasownika (córka). */
  female?: boolean;
  /**
   * Auto-dismiss po N ms. Gdy 0 lub undefined — modal jest "sticky" i
   * zamyka się tylko po kliknięciu CTA / overlay.
   */
  autoDismissMs?: number;
};

/**
 * Fullscreen overlay maskotki dla key zdarzeń (level-up, task-submitted,
 * badge-unlocked, etc.). Wybiera emocję + ton + intensywność z
 * CONTEXT_PRESETS, kompozuje copy przez momentCopy().
 *
 * Animacja wejścia: fade-in + scale-in (300ms). Wyjście: fade-out.
 */
export function MascotMoment({
  context,
  open,
  onClose,
  data,
  female = false,
  autoDismissMs,
}: Props) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open || !autoDismissMs) return;
    const t = setTimeout(onClose, autoDismissMs);
    return () => clearTimeout(t);
  }, [open, autoDismissMs, onClose]);

  // Block body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset wieloekranowego stanu przy każdym otwarciu.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  if (!open) return null;

  const preset = CONTEXT_PRESETS[context];
  const copy = momentCopy(context, data, female);
  const textColor = toneTextColor(preset.tone);
  const isLight = textColor === "navy";
  const showConfetti = preset.intensity === "big" && CONFETTI_CONTEXTS.has(context);

  // Mascot size: ambient < small < big
  const mascotSize =
    preset.intensity === "big" ? 220 : preset.intensity === "ambient" ? 120 : 160;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center justify-center px-6 pt-5 pb-10",
        "animate-in fade-in duration-300",
      )}
      style={{ background: toneBackground(preset.tone) }}
    >
      {showConfetti && <Confetti />}

      {/* Tap-outside scrim to close (only when not auto-dismiss) */}
      <button
        type="button"
        aria-label="Zamknij"
        onClick={onClose}
        className="absolute inset-0 z-0"
      />

      {copy.steps ? (
        <MultiStepContent
          steps={copy.steps}
          step={step}
          parentGender={data?.parentGender ?? null}
          isLight={isLight}
          onAdvance={() => {
            if (step < copy.steps!.length - 1) setStep(step + 1);
            else onClose();
          }}
        />
      ) : (
        <div
          className={cn(
            "relative z-[1] flex flex-col items-center text-center max-w-sm w-full",
            "animate-in zoom-in-95 fade-in duration-500 ease-out",
          )}
        >
          {/* Mascot — dla parent-philosophy używamy maskotki rodzica
              (gendered Mama/Tata), inaczej Sianko. */}
          <div className="mb-3">
            {context === "parent-philosophy" ? (
              <ParentMascot
                gender={data?.parentGender ?? null}
                size={mascotSize}
                shadow={!isLight}
              />
            ) : (
              <Mascot
                emotion={preset.emotion}
                size={mascotSize}
                shadow={!isLight}
              />
            )}
          </div>

          {/* Headline */}
          <h2
            className={cn(
              "font-display text-[28px] font-extrabold leading-tight tracking-tight",
              isLight ? "text-navy" : "text-white",
            )}
          >
            {copy.headline}
          </h2>

          {/* Sub */}
          <p
            className={cn(
              "mt-2 text-[14px] leading-snug max-w-[28ch]",
              isLight ? "text-slate" : "text-white/85",
            )}
          >
            {copy.sub}
          </p>

          {/* Optional reward chip — animowany counter rolling up */}
          {data?.rewardMinor != null && data.rewardMinor > 0 && (
            <RewardChip rewardMinor={data.rewardMinor} isLight={isLight} />
          )}

          {/* CTA */}
          <div className="mt-6 w-full max-w-[220px]">
            <Button
              variant={isLight ? "primary" : "sunny"}
              size="lg"
              block
              onClick={onClose}
            >
              {copy.cta}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Wieloekranowy moment — bubble nad maskotką, przycisk pod. Krok 0 pokazuje
 * rodzica zalogowanego, krok 1 drugiego rodzica (dialog między dwoma
 * rodzicami). Animacja crossfade'u na zmianę kroku przez `key={step}`.
 */
function MultiStepContent({
  steps,
  step,
  parentGender,
  isLight,
  onAdvance,
}: {
  steps: MomentStep[];
  step: number;
  parentGender: "m" | "f" | null;
  isLight: boolean;
  onAdvance: () => void;
}) {
  const baseGender: "m" | "f" = parentGender === "f" ? "f" : "m";
  const oppositeGender: "m" | "f" = baseGender === "f" ? "m" : "f";
  const current = steps[step];

  if (current.kind === "cards") {
    return (
      <div
        key={step}
        className={cn(
          "relative z-[1] flex flex-col items-center text-center w-full max-w-sm",
          "max-h-[92dvh] overflow-y-auto px-1",
          "animate-in zoom-in-95 fade-in duration-500 ease-out",
        )}
      >
        <img
          src="/family-misja.png"
          alt=""
          aria-hidden
          className="w-32 h-auto -mb-1 select-none pointer-events-none"
        />
        <h2
          className={cn(
            "font-display text-[22px] font-extrabold leading-tight tracking-tight",
            isLight ? "text-navy" : "text-white",
          )}
        >
          {current.headline}
        </h2>
        {current.sub && (
          <p
            className={cn(
              "mt-1 text-[13px] leading-snug max-w-[34ch]",
              isLight ? "text-slate" : "text-white/85",
            )}
          >
            {current.sub}
          </p>
        )}
        <div className="mt-3 w-full text-left">
          <PhilosophyCards variant="compact" />
        </div>
        <a
          href={BLOG_PHILOSOPHY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "mt-3 text-[12.5px] underline decoration-dotted underline-offset-4",
            isLight ? "text-slate hover:text-ink" : "text-white/85 hover:text-white",
          )}
        >
          Przeczytaj cały wpis na blogu ↗
        </a>
        <div className="mt-4 w-full max-w-[260px]">
          <Button
            variant={isLight ? "primary" : "sunny"}
            size="lg"
            block
            onClick={onAdvance}
          >
            {current.cta}
          </Button>
        </div>
      </div>
    );
  }

  const currentGender = step === 0 ? baseGender : oppositeGender;

  return (
    <div
      key={step}
      className={cn(
        "relative z-[1] flex flex-col items-center text-center max-w-sm w-full gap-4",
        "animate-in zoom-in-95 fade-in duration-500 ease-out",
      )}
    >
      <SpeechBubble
        tail="bottom-center"
        className={cn("max-w-[30ch]", step === 0 && "font-bold")}
      >
        {current.bubble}
      </SpeechBubble>

      <ParentMascot
        gender={currentGender}
        pose="info"
        size={320}
        shadow={false}
      />

      <div className="mt-2 w-full max-w-[220px]">
        <Button
          variant={isLight ? "primary" : "sunny"}
          size="lg"
          block
          onClick={onAdvance}
        >
          {current.cta}
        </Button>
      </div>
    </div>
  );
}

/**
 * Chip z animowanym licznikiem nagrody — rolling up od 0 do `rewardMinor`
 * w 600ms. Coin emoji ma subtle bounce w pierwszej pętli animacji.
 */
function RewardChip({
  rewardMinor,
  isLight,
}: {
  rewardMinor: number;
  isLight: boolean;
}) {
  const animated = useAnimatedNumber(rewardMinor, 700);
  const formatted = (animated / 100).toFixed(2).replace(".", ",");

  return (
    <div
      className={cn(
        "mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2",
        isLight ? "bg-white shadow-bubble" : "bg-white/15 backdrop-blur",
        "animate-in zoom-in-95 fade-in duration-500",
      )}
    >
      <span className="text-2xl" aria-hidden>
        🪙
      </span>
      <span
        className={cn(
          "font-num text-lg font-extrabold tabular-nums",
          isLight ? "text-money" : "text-white",
        )}
      >
        +{formatted} zł
      </span>
    </div>
  );
}
